// ReportModal
// -----------
// Modal for generating a PDF pentest report.
// Clicking a report type immediately generates it — no extra button needed.

import { useCallback, useRef } from "react";
import { X, FileText, EyeOff } from "lucide-react";
import ReactDOM from "react-dom";
import { Button } from "@/components/ui/button";
import WebApplicationClass from "@/models/webApplication";
import { useModalAccessibility } from "@/utils/useModalAccessibility";

const REPORT_OPTIONS = [
  {
    type: "full",
    icon: FileText,
    label: "Full Report",
    description: "Complete findings, evidence, and recommendations.",
  },
  {
    type: "redacted",
    icon: EyeOff,
    label: "Redacted Report",
    description:
      "A shareable summary that confirms a penetration test was performed, without disclosing any vulnerability details.",
  },
];

interface ReportModalProps {
  webapp: WebApplicationClass;
  show: boolean;
  close: () => void;
  onGenerate: (reportType: string, password: string | null) => void;
}

export default function ReportModal({ webapp, show, close, onGenerate }: ReportModalProps) {
  const handleSelect = useCallback((reportType: string) => {
    onGenerate(reportType, null);
    close();
  }, [onGenerate, close]);

  const dialogContainerRef = useRef<HTMLDivElement>(null);
  useModalAccessibility(show, close, dialogContainerRef);

  if (!show) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={close}
    >
      <div className="absolute inset-0 bg-black/40" />

      <div
        ref={dialogContainerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Generate Report"
        className="relative w-full max-w-md rounded-xl bg-white shadow-xl"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Generate Report</h2>
          <button
            type="button"
            onClick={close}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 flex flex-col gap-3">
          <p className="text-sm text-gray-500">
            Generate a report for <span className="font-medium text-gray-700">{webapp.name}</span>.
          </p>

          {REPORT_OPTIONS.map(({ type, icon: Icon, label, description }) => (
            <button
              key={type}
              type="button"
              onClick={() => handleSelect(type)}
              className="flex items-start gap-3 w-full rounded-lg border border-gray-200 px-4 py-3 text-left transition-all cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 hover:ring-1 hover:ring-blue-400"
            >
              <div className="rounded-md p-1.5 shrink-0 mt-0.5 bg-gray-100 text-gray-500">
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{description}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Footer — cancel only */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={close}>
            Cancel
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
